import moment from 'moment';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  BookingPatient,
  ContactUser,
  FamilyMember,
  IFamilyBookingPatient,
  IResolvedAvailability,
  SelectedPatient,
  SlotsPerDate,
} from 'interfaces/familyBookingTypes';
import { Insurance } from 'interfaces/insuranceType';
import { ModeOption } from 'pages/ClinicDetailsPage/BookNowPanel/utils/constants';
import {
  checkIsMatchingPatientRecord,
  checkIsSamePatient,
} from 'utils/checkIsSamePatient';
import { getPatientKey } from 'utils/getPatientKey';

import { getCountryCodeByIPThunk } from './bookingFormSlice';

export const DEFAULT_COUNTRY_CODE = 'ca';

export const FAMILY_BOOKING_NOTE_MAX_LENGTH = 255;

export const STEPS = {
  CONTACT_LOOKUP: '1.CONTACT_LOOKUP',
  VERIFICATION: '2.VERIFICATION',
  SIGN_IN: '2.SIGN_IN',
  SIGN_UP: '2.SIGN_UP',
  CONTACT_DETAILS: '2.CONTACT_DETAILS',
  SCHEDULE: '3.SCHEDULE',
  SLOT_SELECTION: '3.SLOT_SELECTION',
} as const;

export type FamilyStep = (typeof STEPS)[keyof typeof STEPS];

export const FIRST_FAMILY_STEP: FamilyStep = STEPS.CONTACT_LOOKUP;

interface Step1 {
  phoneNumber: string;
  dob: string;
  countryCode: string;
}

interface Step2 {
  contactPatient: ContactPatient;
  signUp: SignUp;
}

export interface SignUp {
  firstName: string;
  lastName: string;
  email: string;
  dob: string;
  phoneNumber: string;
  countryCode: string;
}

interface ContactPatient {
  firstName: string;
  lastName: string;
  email: string;
  serviceId?: string | null;
  insurance: Insurance | null;
  isPayForMyself: boolean;
  insurancePaymentMethod: ModeOption | null;
}

interface FamilyBookingState {
  cachedData: Omit<Step1, 'countryCode'>;
  step1: Step1;
  step2: Step2;
  stepStack: FamilyStep[];
  isReviewOpen: boolean;
  patients: BookingPatient[];
  contactUser: ContactUser;
  selectedPatients: SelectedPatient[];
  slotsPerDate: SlotsPerDate | null;
  cachedPatientAvailability: Record<string, IResolvedAvailability>;
  currentSelectedPatientIndex: number;
  currentSelectedDate: string | null;
  familyBookingSessionId: string | null;
  note: string;
  homeAddress: string;
  postalCode: string;
}

const initialState: FamilyBookingState = {
  cachedData: {
    phoneNumber: '',
    dob: '',
  },
  step1: {
    phoneNumber: '',
    dob: '',
    countryCode: DEFAULT_COUNTRY_CODE,
  },
  step2: {
    contactPatient: {
      firstName: '',
      lastName: '',
      email: '',
      insurance: null,
      isPayForMyself: false,
      insurancePaymentMethod: null,
    },
    signUp: {
      firstName: '',
      lastName: '',
      email: '',
      dob: '',
      phoneNumber: '',
      countryCode: DEFAULT_COUNTRY_CODE,
    },
  },
  stepStack: [FIRST_FAMILY_STEP],
  isReviewOpen: false,
  patients: [],
  contactUser: {
    phoneNumber: '',
    dob: '',
    firstName: '',
    lastName: '',
    email: '',
  },
  selectedPatients: [],
  slotsPerDate: null,
  cachedPatientAvailability: {},
  currentSelectedDate: null,
  currentSelectedPatientIndex: 0,
  familyBookingSessionId: null,
  note: '',
  homeAddress: '',
  postalCode: '',
};

const clearPatientSlotFields = (patient: SelectedPatient): SelectedPatient => ({
  ...patient,
  appointmentDate: null,
  startTime: null,
  doctorId: null,
  operatoryId: null,
  dentist: null,
  childServiceId: null,
  chairOptimizations: null,
  bookingServiceId: null,
  practitionerName: null,
  patientFamilyBookingSessionId: null,
  isSlotSelected: false,
});

const clearScheduleSelectionFields = <T extends FamilyMember>(
  entity: T
): T => ({
  ...entity,
  serviceId: null,
  isDisabledService: false,
  preferredPractitionerId: null,
  preferredPractitionerTitle: null,
  preferredDentistId: null,
  preferredDentistTitle: null,
});

const familyBookingSlice = createSlice({
  name: 'familyBooking',
  initialState,
  reducers: {
    resetFamilyBooking: (state) => {
      state.step1 = {
        ...initialState.step1,
        countryCode: state.step1.countryCode,
      };

      state.step2 = {
        contactPatient: {
          ...initialState.step2.contactPatient,
        },
        signUp: {
          ...initialState.step2.signUp,
          countryCode: state.step2.signUp.countryCode,
        },
      };

      state.cachedData = initialState.cachedData;
      state.stepStack = initialState.stepStack;
      state.isReviewOpen = initialState.isReviewOpen;
      state.familyBookingSessionId = null;
      state.note = initialState.note;
      state.homeAddress = initialState.homeAddress;
      state.postalCode = initialState.postalCode;
      state.selectedPatients = state.selectedPatients.map((patient) => ({
        ...patient,
        patientFamilyBookingSessionId: null,
      }));

      state.cachedPatientAvailability = {};
    },
    resetForReverification: (state) => {
      const isSignUpIdentity = state.stepStack.includes(STEPS.SIGN_UP);

      state.stepStack = isSignUpIdentity
        ? [STEPS.CONTACT_LOOKUP, STEPS.SIGN_UP, STEPS.VERIFICATION]
        : [STEPS.CONTACT_LOOKUP, STEPS.CONTACT_DETAILS, STEPS.VERIFICATION];

      state.isReviewOpen = false;
      state.note = initialState.note;
      state.homeAddress = initialState.homeAddress;
      state.postalCode = initialState.postalCode;

      state.patients = state.patients
        .filter((patient) => !!patient.id)
        .map(clearScheduleSelectionFields);

      state.contactUser = clearScheduleSelectionFields(state.contactUser);
      state.selectedPatients = [];
      state.slotsPerDate = null;
      state.cachedPatientAvailability = {};
      state.currentSelectedPatientIndex =
        initialState.currentSelectedPatientIndex;
      state.currentSelectedDate = initialState.currentSelectedDate;
      state.familyBookingSessionId = null;
    },
    updateFBStep1: (state, action: PayloadAction<Partial<Step1>>) => {
      state.step1 = { ...state.step1, ...action.payload };
    },
    updateFBStep2: (state, action: PayloadAction<Partial<Step2>>) => {
      if (action.payload.contactPatient) {
        state.step2.contactPatient = action.payload.contactPatient;
      }

      if (action.payload.signUp) {
        state.step2.signUp = {
          ...state.step2.signUp,
          ...action.payload.signUp,
        };
      }
    },
    updateContactPatient: (
      state,
      action: PayloadAction<Partial<ContactPatient>>
    ) => {
      state.step2.contactPatient = {
        ...state.step2.contactPatient,
        ...action.payload,
      };
    },
    resetFBStep2SignUp: (state) => {
      state.step2.signUp = {
        ...initialState.step2.signUp,
        countryCode: state.step2.signUp.countryCode,
      };
    },
    resetStep2ContactPatient: (state) => {
      state.step2.contactPatient = initialState.step2.contactPatient;
    },

    updateFBCached: (
      state,
      action: PayloadAction<Omit<Step1, 'countryCode'>>
    ) => {
      state.cachedData = action.payload;
    },
    resetFBCached: (state) => {
      state.cachedData = initialState.cachedData;
    },
    popFamilyStep: (state) => {
      state.stepStack.pop();
    },
    pushFamilyStep: (state, action: PayloadAction<FamilyStep>) => {
      const stepStack = state.stepStack;
      const currentPage = stepStack[stepStack.length - 1];

      if (currentPage === STEPS.VERIFICATION) {
        stepStack.pop();
      }

      stepStack.push(action.payload);
    },
    setReviewOpen: (state, action: PayloadAction<boolean>) => {
      state.isReviewOpen = action.payload;
    },
    setFamilyBookingNote: (state, action: PayloadAction<string>) => {
      state.note = action.payload;
    },

    setFamilyBookingHomeAddress: (state, action: PayloadAction<string>) => {
      state.homeAddress = action.payload;
    },

    setFamilyBookingPostalCode: (state, action: PayloadAction<string>) => {
      state.postalCode = action.payload;
    },
    updateContactUser: (state, action: PayloadAction<Partial<ContactUser>>) => {
      state.contactUser = {
        ...state.contactUser,
        ...action.payload,
      };
    },

    setContactUser: (state, action: PayloadAction<ContactUser>) => {
      const matchedPatient = state.patients.find((patient) =>
        checkIsMatchingPatientRecord(patient, action.payload)
      );

      state.contactUser = {
        ...action.payload,
        id: matchedPatient?.id ?? action.payload.id ?? null,
        patientServices:
          matchedPatient?.patientServices ?? action.payload.patientServices,
        isBookable: matchedPatient?.isBookable ?? action.payload.isBookable,
        homeAddress: matchedPatient?.homeAddress ?? action.payload.homeAddress,
        isExistingApptWithinDays:
          matchedPatient?.isExistingApptWithinDays ??
          action.payload.isExistingApptWithinDays,
        futureAppointments:
          matchedPatient?.futureAppointments ??
          action.payload.futureAppointments,
        lastHygieneAppt:
          matchedPatient?.lastHygieneAppt ?? action.payload.lastHygieneAppt,
      };
    },

    resetContactUserSchedule: (state) => {
      state.contactUser = clearScheduleSelectionFields(state.contactUser);
    },

    updatePatients: (state, action: PayloadAction<IFamilyBookingPatient[]>) => {
      state.patients = action.payload.map((item) => ({
        id: item.id,
        firstName: item.firstName,
        lastName: item.lastName,
        dob: item.dob,
        email: item.email,
        phoneNumber: item.phoneNumber,
        patientServices: item.services,
        //--The mapper reports an absent value as undefined, never null--
        homeAddress: item.homeAddress ?? undefined,
        isBookable: item.isBookable,
        isExistingApptWithinDays: item.isExistingApptWithinDays,
        futureAppointments: item.futureAppointments,
        lastHygieneAppt: item.lastHygieneAppt ?? undefined,
      }));
    },

    updateBookingPatient: (state, action: PayloadAction<BookingPatient>) => {
      state.patients = state.patients.map((currentPatient) => {
        if (checkIsSamePatient(currentPatient, action.payload)) {
          return { ...currentPatient, ...action.payload };
        }

        return currentPatient;
      });
    },

    addBookingPatient: (state, action: PayloadAction<BookingPatient>) => {
      const isExistingBookingPatient = state.patients.some((patient) =>
        checkIsMatchingPatientRecord(patient, action.payload)
      );

      if (!isExistingBookingPatient) {
        state.patients.push(action.payload);
      }
    },

    updateSelectedPatients: (
      state,
      action: PayloadAction<Omit<SelectedPatient, 'isSlotSelected'>[]>
    ) => {
      state.selectedPatients = action.payload.map((item) => ({
        ...item,
        dob: moment(item.dob, ['YYYY-MM-DD', 'MM/DD/YYYY']).format(
          'YYYY-MM-DD'
        ),
        isSlotSelected: false,
      }));

      state.cachedPatientAvailability = {};
    },

    resetSelectedPatients: (state) => {
      state.selectedPatients = state.selectedPatients.map(
        clearPatientSlotFields
      );
      state.familyBookingSessionId = null;
      state.cachedPatientAvailability = {};
    },

    mergeSlotsPerDate: (state, action: PayloadAction<SlotsPerDate>) => {
      state.slotsPerDate = {
        ...(state.slotsPerDate || {}),
        ...action.payload,
      };
    },

    cachePatientAvailability: (
      state,
      action: PayloadAction<IResolvedAvailability>
    ) => {
      state.cachedPatientAvailability[action.payload.patientKey] =
        action.payload;
    },

    invalidatePatientAvailability: (state, action: PayloadAction<string>) => {
      delete state.cachedPatientAvailability[action.payload];
    },

    updatedCurrentSelectedDate: (state, action: PayloadAction<string>) => {
      state.currentSelectedDate = action.payload;
    },

    updatedCurrentSelectedPatientIndex: (
      state,
      action: PayloadAction<number>
    ) => {
      state.currentSelectedPatientIndex = action.payload;
    },

    updateSelectedPatientAtIndex: (
      state,
      action: PayloadAction<{
        index: number;
        patient: Partial<SelectedPatient>;
      }>
    ) => {
      const { index, patient } = action.payload;
      const selectedPatient = state.selectedPatients[index];

      if (!selectedPatient) return;

      state.selectedPatients[index] = {
        ...selectedPatient,
        ...patient,
        isSlotSelected: true,
      };
    },

    resetSelectedPatientAtIndex: (state, action: PayloadAction<number>) => {
      const selectedPatient = state.selectedPatients[action.payload];

      if (!selectedPatient) return;

      state.selectedPatients[action.payload] =
        clearPatientSlotFields(selectedPatient);
    },

    resetSelectedPatientsFromIndex: (state, action: PayloadAction<number>) => {
      state.selectedPatients.forEach((patient, index) => {
        if (index <= action.payload) return;

        delete state.cachedPatientAvailability[getPatientKey(patient)];
        state.selectedPatients[index] = clearPatientSlotFields(patient);
      });
    },

    updateFamilyBookingSessionId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.familyBookingSessionId = action.payload;
    },

    clearFamilyBookingReservedSlots: (state) => {
      state.familyBookingSessionId = null;
      state.selectedPatients = state.selectedPatients.map((patient) => ({
        ...patient,
        patientFamilyBookingSessionId: null,
      }));
    },

    resetPatientSelections: (state) => {
      state.patients = state.patients
        .filter((patient) => !!patient.id)
        .map(clearScheduleSelectionFields);

      state.contactUser = clearScheduleSelectionFields(state.contactUser);

      state.selectedPatients = [];
      state.cachedPatientAvailability = {};
      state.currentSelectedPatientIndex =
        initialState.currentSelectedPatientIndex;
      state.currentSelectedDate = initialState.currentSelectedDate;
      state.familyBookingSessionId = null;
    },

    clearBookingPatient: (state, action: PayloadAction<BookingPatient>) => {
      state.patients = state.patients.map((currentPatient) => {
        if (checkIsSamePatient(currentPatient, action.payload)) {
          return { ...currentPatient, serviceId: null };
        }

        return { ...currentPatient };
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCountryCodeByIPThunk.fulfilled, (state, action) => {
      state.step1.countryCode = action.payload;
      state.step2.signUp.countryCode = action.payload;
    });
  },
});

export const {
  resetFamilyBooking,
  resetForReverification,
  updateFBStep1,
  updateFBStep2,
  updateContactPatient,
  resetFBStep2SignUp,
  resetStep2ContactPatient,
  updatePatients,
  updateFBCached,
  resetFBCached,
  popFamilyStep,
  pushFamilyStep,
  setReviewOpen,
  setFamilyBookingNote,
  setFamilyBookingHomeAddress,
  setFamilyBookingPostalCode,
  updateBookingPatient,
  addBookingPatient,
  clearBookingPatient,
  resetPatientSelections,
  updateContactUser,
  setContactUser,
  resetContactUserSchedule,
  updateSelectedPatients,
  mergeSlotsPerDate,
  cachePatientAvailability,
  invalidatePatientAvailability,
  updatedCurrentSelectedDate,
  updatedCurrentSelectedPatientIndex,
  updateSelectedPatientAtIndex,
  resetSelectedPatientAtIndex,
  resetSelectedPatientsFromIndex,
  resetSelectedPatients,
  updateFamilyBookingSessionId,
  clearFamilyBookingReservedSlots,
} = familyBookingSlice.actions;

export default familyBookingSlice.reducer;
